import Axhelper from '@/lib/Axhelper'
import PlacowkaService from '@/services/PlacowkaService'

class KampaniePlacowkiService {
	constructor() {
		this.baseUrl = window.RUNTIME_CONFIG.API_URL
		this.endpoints = {
			kampaniePlacowkiFilters: '/api/kampanieplacowki/filters',
			kampaniePlacowkiDetails: '/api/kampanieplacowki/details',
			kampaniePlacowkiSearch: '/api/kampanieplacowki/search',
			kampaniePlacowkiCreate: '/api/kampanieplacowki',
			kampaniePlacowkiUpdate: '/api/kampanieplacowki',
			kampaniePlacowkiDelete: '/api/kampanieplacowki',
			kampaniePlacowkiMarkAsDone: '/api/kampanieplacowki/markasdone',
			kampaniePlacowkiMarkAsUndone: '/api/kampanieplacowki/markasundone',
			kampaniePlacowkiAddToCampaign: '/api/kampanieplacowki/addtocampaign',
			kampaniePlacowkiRemoveFromCampaign: '/api/kampanieplacowki/removefromcampaign',
			kampaniePlacowkiAddFromFilterCount: '/api/kampanieplacowki/addFromFilterCount',
			kampaniePlacowkiGetByPlacowka: '/api/kampanieplacowki/getbyplacowka',
		}
	}

	_buildPlacowkaFilterQueryParams(searchText = '', filters = {}) {
		const params = new URLSearchParams()
		const trimmedSearch = (searchText || '').trim()
		if (trimmedSearch) {
			params.append('searchText', trimmedSearch)
		}
		if (filters?.id_miasto) {
			params.append('id_miasto', String(filters.id_miasto))
		}
		if (filters?.id_gmina) {
			params.append('id_gmina', String(filters.id_gmina))
		}
		if (filters?.id_powiat) {
			params.append('id_powiat', String(filters.id_powiat))
		}
		if (filters?.id_wojewodztwo) {
			params.append('id_wojewodztwo', String(filters.id_wojewodztwo))
		}
		if (filters?.id_typ) {
			params.append('id_typ', String(filters.id_typ))
		}
		if (filters?.triage !== null && filters?.triage !== undefined && filters?.triage !== '') {
			params.append('triage', String(filters.triage))
		}
		return params
	}

	_buildCampaignPlacowkaIdsPayload(kampaniaId, placowkaIds) {
		const params = new URLSearchParams()
		params.append('kampaniaId', String(kampaniaId))
		const ids = Array.isArray(placowkaIds) ? placowkaIds : [placowkaIds]
		ids.filter(Boolean).forEach((placowkaId, index) => {
			params.append(`placowkaIds[${index}]`, String(placowkaId))
		})
		return params
	}

	_buildFilterParams(options = {}) {
		const {
			limit = 50,
			sortColumn = '_id',
			sortDirection = 'desc',
			searchText = '',
			filters = {},
		} = options

		const params = new URLSearchParams({
			sortcol: sortColumn,
			sortdir: sortDirection,
			limit: String(limit),
		})

		const trimmedSearch = (searchText || '').trim()
		if (trimmedSearch) {
			params.append('searchText', trimmedSearch)
		}
		if (filters?.kampaniaId) {
			params.append('kampaniaId', String(filters.kampaniaId))
		}
		if (filters?.placowkaId) {
			params.append('placowkaId', String(filters.placowkaId))
		}
		if (filters?.id_miasto) {
			params.append('id_miasto', String(filters.id_miasto))
		}
		if (filters?.id_gmina) {
			params.append('id_gmina', String(filters.id_gmina))
		}
		if (filters?.id_powiat) {
			params.append('id_powiat', String(filters.id_powiat))
		}
		if (filters?.id_wojewodztwo) {
			params.append('id_wojewodztwo', String(filters.id_wojewodztwo))
		}
		if (filters?.id_typ) {
			params.append('id_typ', String(filters.id_typ))
		}
		if (filters?.triage !== null && filters?.triage !== undefined && filters?.triage !== '') {
			params.append('triage', String(filters.triage))
		}

		return params
	}

	_parseListResponse(response, rows = null) {
		const data = rows ?? (Array.isArray(response.data) ? response.data : [])
		const totalCount = response.headers?.['x-total-count']
			? parseInt(response.headers['x-total-count'], 10)
			: data.length

		return {
			data,
			error: null,
			totalCount,
			hasMore: data.length < totalCount,
			headers: response.headers,
		}
	}

	async getByPlacowka(placowkaId, options = {}) {
		try {
			if (!placowkaId) {
				return {
					data: [],
					error: 'Invalid placowka ID provided',
					totalCount: 0,
					hasMore: false,
				}
			}

			const params = this._buildFilterParams(options)
			const url = `${this.endpoints.kampaniePlacowkiGetByPlacowka}/${encodeURIComponent(placowkaId)}?${params.toString()}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: [],
					error: response.error,
					totalCount: 0,
					hasMore: false,
				}
			}

			return this._parseListResponse(response)
		} catch (error) {
			console.error('KampaniePlacowkiService.getByPlacowka error:', error)
			return {
				data: [],
				error: error.message || 'Failed to fetch kampanie for placowka',
				totalCount: 0,
				hasMore: false,
			}
		}
	}

	async getFiltered(options = {}) {
		try {
			const params = this._buildFilterParams(options)
			const url = `${this.endpoints.kampaniePlacowkiFilters}?${params.toString()}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: [],
					error: response.error,
					totalCount: 0,
					hasMore: false,
				}
			}

			return this._parseListResponse(response)
		} catch (error) {
			console.error('KampaniePlacowkiService.getFiltered error:', error)
			return {
				data: [],
				error: error.message || 'Failed to fetch kampanie placowki',
				totalCount: 0,
				hasMore: false,
			}
		}
	}

	async getById(kampaniePlacowkaId) {
		try {
			if (!kampaniePlacowkaId) {
				return {
					data: null,
					error: 'Invalid kampanie placowka ID provided',
				}
			}

			const url = `${this.endpoints.kampaniePlacowkiDetails}/${encodeURIComponent(kampaniePlacowkaId)}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: null,
					error: response.error,
				}
			}

			return {
				data: response.data || null,
				error: null,
			}
		} catch (error) {
			console.error('KampaniePlacowkiService.getById error:', error)
			return {
				data: null,
				error: error.message || 'Failed to fetch kampanie placowka details',
			}
		}
	}

	async search(searchTerm, options = {}) {
		try {
			const trimmed = (searchTerm || '').trim()
			if (!trimmed) {
				return {
					data: [],
					error: 'Invalid search term provided',
					totalCount: 0,
					hasMore: false,
				}
			}

			const params = this._buildFilterParams({
				...options,
				searchText: trimmed,
			})
			const url = `${this.endpoints.kampaniePlacowkiFilters}?${params.toString()}`

			const looksLikeObjectId = /^[a-fA-F0-9]{24}$/.test(trimmed)
			const [response, byIdResult] = await Promise.all([
				Axhelper.sendGetAsync(url),
				looksLikeObjectId ? this.getById(trimmed) : Promise.resolve({ data: null, error: null }),
			])

			let rows = []
			if (!response.error) {
				rows = Array.isArray(response.data) ? [...response.data] : []
			}

			if (looksLikeObjectId && byIdResult?.data && !byIdResult.error) {
				const doc = byIdResult.data
				const docId = doc?._id ?? doc?.id
				const already = rows.some(item => String(item?._id ?? item?.id) === String(docId))
				if (!already) {
					rows.unshift(doc)
				}
			}

			if (response.error && rows.length === 0) {
				return {
					data: [],
					error: response.error,
					totalCount: 0,
					hasMore: false,
				}
			}

			return this._parseListResponse(response, rows)
		} catch (error) {
			console.error('KampaniePlacowkiService.search error:', error)
			return {
				data: [],
				error: error.message || 'Failed to search kampanie placowki',
				totalCount: 0,
				hasMore: false,
			}
		}
	}

	async markAsUndone(kampaniePlacowkaId) {
		try {
			if (!kampaniePlacowkaId) {
				return {
					success: false,
					error: 'Invalid kampanie placowka ID provided',
				}
			}

			const url = `${this.endpoints.kampaniePlacowkiMarkAsUndone}/${encodeURIComponent(kampaniePlacowkaId)}`
			const response = await Axhelper.sendPutAsync(url, {})

			if (response.error) {
				return {
					success: false,
					error: response.error,
				}
			}

			return {
				success: true,
				error: null,
				data: response.data || null,
			}
		} catch (error) {
			console.error('KampaniePlacowkiService.markAsUndone error:', error)
			return {
				success: false,
				error: error.message || 'Failed to mark kampanie placowka as undone',
			}
		}
	}

	async markAsDone(kampaniePlacowkaId, doneDate = '') {
		try {
			if (!kampaniePlacowkaId) {
				return {
					success: false,
					error: 'Invalid kampanie placowka ID provided',
				}
			}

			const url = `${this.endpoints.kampaniePlacowkiMarkAsDone}/${encodeURIComponent(kampaniePlacowkaId)}`
			const payload = doneDate ? { doneDate } : {}
			const response = await Axhelper.sendPutAsync(url, payload)

			if (response.error) {
				return {
					success: false,
					error: response.error,
				}
			}

			return {
				success: true,
				error: null,
				data: response.data || null,
			}
		} catch (error) {
			console.error('KampaniePlacowkiService.markAsDone error:', error)
			return {
				success: false,
				error: error.message || 'Failed to mark kampanie placowka as done',
			}
		}
	}

	async addFromFilterCount(options = {}) {
		try {
			const { searchText = '', filters = {} } = options
			const params = this._buildPlacowkaFilterQueryParams(searchText, filters)
			const url = `${this.endpoints.kampaniePlacowkiAddFromFilterCount}?${params.toString()}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					count: 0,
					error: response.error,
				}
			}

			const raw = response.data
			const count =
				typeof raw === 'number'
					? raw
					: typeof raw?.count === 'number'
						? raw.count
						: parseInt(raw, 10) || 0

			return {
				count,
				error: null,
			}
		} catch (error) {
			console.error('KampaniePlacowkiService.addFromFilterCount error:', error)
			return {
				count: 0,
				error: error.message || 'Failed to fetch filter count',
			}
		}
	}

	async _fetchPlacowkiForFilter(options = {}) {
		const { searchText = '', filters = {} } = options
		const requestOptions = {
			limit: 1000,
			sortColumn: '_id',
			sortDirection: 'desc',
			filters: {
				id_miasto: filters?.id_miasto || null,
				id_gmina: filters?.id_gmina || null,
				id_powiat: filters?.id_powiat || null,
				id_wojewodztwo: filters?.id_wojewodztwo || null,
				id_typ: filters?.id_typ || null,
				triage:
					filters?.triage !== null && filters?.triage !== undefined && filters?.triage !== ''
						? filters.triage
						: null,
			},
		}
		const trimmedSearch = (searchText || '').trim()
		return trimmedSearch
			? PlacowkaService.search(trimmedSearch, requestOptions)
			: PlacowkaService.getAll(requestOptions)
	}

	async addToCampaignFromFilter(kampaniaId, options = {}) {
		try {
			if (!kampaniaId) {
				return {
					success: false,
					error: 'Invalid kampania ID provided',
					data: null,
				}
			}

			const placowkiResult = await this._fetchPlacowkiForFilter(options)
			if (placowkiResult.error) {
				return {
					success: false,
					error: placowkiResult.error,
					data: null,
				}
			}

			const placowkaIds = (placowkiResult.data || [])
				.map(item => item?._id ?? item?.id)
				.filter(Boolean)

			if (placowkaIds.length === 0) {
				return {
					success: false,
					error: 'Brak placowek pasujacych do filtra',
					data: null,
				}
			}

			return this.addToCampaign(kampaniaId, placowkaIds)
		} catch (error) {
			console.error('KampaniePlacowkiService.addToCampaignFromFilter error:', error)
			return {
				success: false,
				error: error.message || 'Failed to add placowki from filter to campaign',
				data: null,
			}
		}
	}

	async addToCampaign(kampaniaId, placowkaIds) {
		try {
			const ids = (Array.isArray(placowkaIds) ? placowkaIds : [placowkaIds]).filter(Boolean)
			if (!kampaniaId) {
				return {
					success: false,
					error: 'Invalid kampania ID provided',
					data: null,
				}
			}
			if (ids.length === 0) {
				return {
					success: false,
					error: 'Invalid placowka ID provided',
					data: null,
				}
			}

			const payload = this._buildCampaignPlacowkaIdsPayload(kampaniaId, ids)
			const response = await Axhelper.sendPostAsync(this.endpoints.kampaniePlacowkiAddToCampaign, payload)

			if (response.error) {
				return {
					success: false,
					error: response.error,
					data: null,
				}
			}

			return {
				success: true,
				error: null,
				data: response.data || null,
			}
		} catch (error) {
			console.error('KampaniePlacowkiService.addToCampaign error:', error)
			return {
				success: false,
				error: error.message || 'Failed to add placowka to campaign',
				data: null,
			}
		}
	}

	async removeFromCampaign(kampaniaId, placowkaIds) {
		try {
			const ids = (Array.isArray(placowkaIds) ? placowkaIds : [placowkaIds]).filter(Boolean)
			if (!kampaniaId) {
				return {
					success: false,
					error: 'Invalid kampania ID provided',
					data: null,
				}
			}
			if (ids.length === 0) {
				return {
					success: false,
					error: 'Invalid placowka ID provided',
					data: null,
				}
			}

			const payload = this._buildCampaignPlacowkaIdsPayload(kampaniaId, ids)
			const response = await Axhelper.sendPostAsync(this.endpoints.kampaniePlacowkiRemoveFromCampaign, payload)

			if (response.error) {
				return {
					success: false,
					error: response.error,
					data: null,
				}
			}

			return {
				success: true,
				error: null,
				data: response.data || null,
			}
		} catch (error) {
			console.error('KampaniePlacowkiService.removeFromCampaign error:', error)
			return {
				success: false,
				error: error.message || 'Failed to remove placowka from campaign',
				data: null,
			}
		}
	}

	async delete(kampaniePlacowkaId) {
		try {
			if (!kampaniePlacowkaId) {
				return {
					success: false,
					error: 'Invalid kampanie placowka ID provided',
					message: null,
				}
			}

			const url = `${this.endpoints.kampaniePlacowkiDelete}/${encodeURIComponent(kampaniePlacowkaId)}`
			const response = await Axhelper.sendDeleteAsync(url)

			if (response.error) {
				return {
					success: false,
					error: response.error,
					message: null,
				}
			}

			return {
				success: true,
				error: null,
				message: response.data || 'Kampanie placowka deleted successfully',
			}
		} catch (error) {
			console.error('KampaniePlacowkiService.delete error:', error)
			return {
				success: false,
				error: error.message || 'Failed to delete kampanie placowka',
				message: null,
			}
		}
	}
}

export default new KampaniePlacowkiService()
